/**
 * Extrai as cores dominantes de uma imagem, no browser.
 *
 * A cor da bandeira quase sempre está na foto da fachada, e digitar hex olhando
 * para a foto é onde o erro entra. Quantização por bucket de 4 bits por canal
 * (4096 baldes) — grosseiro de propósito: o objetivo é achar "o azul do posto",
 * não a cor exata do pixel.
 */

const SAMPLE_EDGE = 200;

const toHex = (r: number, g: number, b: number) =>
  `#${[r, g, b].map(v => Math.round(v).toString(16).padStart(2, '0')).join('')}`.toUpperCase();

/** Cinzas e quase-pretos/brancos dominam foto de posto (asfalto, céu, marquise). */
const isInteresting = (r: number, g: number, b: number) => {
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const luminance = 0.299 * r + 0.587 * g + 0.114 * b;
  return (max - min) > 28 && luminance > 24 && luminance < 232;
};

export const extractPalette = async (url: string, count = 5): Promise<string[]> => {
  const img = await new Promise<HTMLImageElement | null>(resolve => {
    const image = new Image();
    image.crossOrigin = 'anonymous';
    image.onload = () => resolve(image);
    image.onerror = () => resolve(null);
    image.src = url;
  });
  if (!img) { return []; }

  const canvas = document.createElement('canvas');
  canvas.width = SAMPLE_EDGE;
  canvas.height = SAMPLE_EDGE;
  const context = canvas.getContext('2d', { willReadFrequently: true });
  if (!context) { return []; }

  context.drawImage(img, 0, 0, SAMPLE_EDGE, SAMPLE_EDGE);

  let data: Uint8ClampedArray;
  try {
    data = context.getImageData(0, 0, SAMPLE_EDGE, SAMPLE_EDGE).data;
  } catch {
    return []; // canvas bloqueado por CORS
  }

  const buckets = new Map<number, { count: number; r: number; g: number; b: number }>();

  for (let i = 0; i < data.length; i += 4) {
    if (data[i + 3] < 200) { continue; }
    const [r, g, b] = [data[i], data[i + 1], data[i + 2]];
    if (!isInteresting(r, g, b)) { continue; }

    const key = ((r >> 4) << 8) | ((g >> 4) << 4) | (b >> 4);
    const bucket = buckets.get(key) || { count: 0, r: 0, g: 0, b: 0 };
    bucket.count += 1;
    bucket.r += r;
    bucket.g += g;
    bucket.b += b;
    buckets.set(key, bucket);
  }

  return [...buckets.values()]
    .sort((a, b) => b.count - a.count)
    .slice(0, count)
    .map(({ count: n, r, g, b }) => toHex(r / n, g / n, b / n));
};
